package com.pincer.app.viewmodel

import kotlin.js.JsName

/*
What changed between two trees, by compound key. The app still rebuilds the whole tree from state on every change
(it's small, and keeping view-model code a plain function of state is what keeps it readable), but renderers and
agents only hear about the difference:

- added:     nodes that are new, parents before their children
- removed:   compound keys that have gone (a removed node's descendants are listed too)
- changed:   nodes whose own fields changed (wording, how it shows, its control, its rich description, its prominence,
             summary or whether it's open, or whether it has children at all, since that changes how most renderers
             draw it)
- reordered: nodes whose children are the same set in a different order

A renderer that redraws only these keeps focus, half-typed text and a screen reader's place when someone else's
change arrives. See docs/design_notes/abstract_client.md.
*/
class TreeDiff(
    val added:     List<Node>,
    val removed:   List<String>,
    val changed:   List<Node>,
    val reordered: List<Node>,
) {

    @JsName("isEmpty")
    fun isEmpty() = added.isEmpty() && removed.isEmpty() && changed.isEmpty() && reordered.isEmpty()

    @JsName("eachAdded")     fun eachAdded(f: (Node) -> Unit)       = added.forEach(f)
    @JsName("eachRemoved")   fun eachRemoved(f: (String) -> Unit)   = removed.forEach(f)
    @JsName("eachChanged")   fun eachChanged(f: (Node) -> Unit)     = changed.forEach(f)
    @JsName("eachReordered") fun eachReordered(f: (Node) -> Unit)   = reordered.forEach(f)

    // A short account for logs and agents, one line per change
    @JsName("describe")
    fun describe(): String {
        val lines = mutableListOf<String>()
        for (node in added)     lines.add("+ ${node.compoundKey()}: ${node.wording}")
        for (key in removed)    lines.add("- $key")
        for (node in changed)   lines.add("~ ${node.compoundKey()}: ${node.wording}")
        for (node in reordered) lines.add("^ ${node.compoundKey()}")
        return lines.joinToString("\n")
    }

    companion object {

        fun between(before: Node?, after: Node): TreeDiff {
            val old = mutableMapOf<String, Node>()
            before?.walk { old[it.compoundKey()] = it }
            val new = mutableMapOf<String, Node>()
            val added = mutableListOf<Node>()
            val changed = mutableListOf<Node>()
            val reordered = mutableListOf<Node>()
            after.walk { node ->
                val key = node.compoundKey()
                new[key] = node
                val was = old[key]
                if (was == null) {
                    added.add(node)
                }
                else {
                    if (!sameOwnFields(was, node)) changed.add(node)
                    val wasOrder = was.children.map { it.key }
                    val nowOrder = node.children.map { it.key }
                    if (wasOrder != nowOrder && wasOrder.toSet() == nowOrder.toSet()) reordered.add(node)
                }
            }
            val removed = old.keys.filter { it !in new }
            return TreeDiff(added = added, removed = removed, changed = changed, reordered = reordered)
        }

        private fun sameOwnFields(a: Node, b: Node) =
            a.key            == b.key &&
            a.wording        == b.wording &&
            a.showAs         == b.showAs &&
            a.controlType    == b.controlType &&
            a.controlChoices == b.controlChoices &&
            a.action         == b.action &&
            a.rich           == b.rich &&
            a.prominence     == b.prominence &&
            a.summary        == b.summary &&
            a.open           == b.open &&
            a.children.isEmpty() == b.children.isEmpty()

    }

}
